import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { getEnv } from "helpers/environment";
import { handleFetchAuth0Token } from "api/ExecutorServiceApi";

export const TIME_RANGES = [
  { label: "Last 5 minutes",  value: "5m",  ms: 5  * 60 * 1000 },
  { label: "Last 15 minutes", value: "15m", ms: 15 * 60 * 1000 },
  { label: "Last 30 minutes", value: "30m", ms: 30 * 60 * 1000 },
  { label: "Last 1 hour",     value: "1h",  ms: 60 * 60 * 1000 },
  { label: "Last 12 hours",   value: "12h", ms: 12 * 60 * 60 * 1000 },
  { label: "Last 24 hours",   value: "24h", ms: 24 * 60 * 60 * 1000 },
  { label: "Last 48 hours",   value: "48h", ms: 48 * 60 * 60 * 1000 },
  { label: "Last 7 days",     value: "7d",  ms: 7  * 24 * 60 * 60 * 1000 },
  { label: "Last 30 days",    value: "30d", ms: 30 * 24 * 60 * 60 * 1000 },
];

const GET_LOGS_QUERY = `
  query GetExecutionLogs(
    $tenantId: String!
    $flowId:   String
    $start:    String
    $end:      String
  ) {
    getExecutionLogs(
      tenantId: $tenantId
      flowId:   $flowId
      start:    $start
      end:      $end
    ) {
      total
      logs {
        id
        time
        line
        labels
        data
      }
    }
  }
`;

function sortById(a, b) {
  const aMs = a.id.split("-")[0];
  const bMs = b.id.split("-")[0];
  if (bMs > aMs) return 1;
  if (bMs < aMs) return -1;
  return 0;
}

function extractLevel(item) {
  const labelLevel =
    item.data?.level   ||
    item.labels?.level ||
    item.labels?.detected_level;
  if (labelLevel) return labelLevel.toLowerCase();
  const lineMatch = item.line?.match(/\[(DEBUG|INFO|WARN|ERROR|FATAL)\s*\]/i);
  if (lineMatch) return lineMatch[1].toLowerCase();
  return "info";
}

function mapLogItem(item) {
  const tsMs = (() => {
    const fromTime = item.time ? new Date(item.time).getTime() : NaN;
    if (!isNaN(fromTime)) return fromTime;
    try {
      return Number(BigInt(String(item.id).split("-")[0]) / 1_000_000n);
    } catch {
      return Date.now();
    }
  })();
  return {
    id:            item.id,
    time:          new Date(item.time).toLocaleString(),
    tsMs,
    line:          item.line,
    labels:        item.labels,
    fields:        item.data || item.labels || {},
    level:         extractLevel(item),
    isParserError: !!item.labels?.__error__,
  };
}

export function useLokiQuery({
  timeRange           = TIME_RANGES[5],
  autoRefreshInterval = 0,
  flowId              = null,
} = {}) {
  const { user } = useAuth0();
  const tenantId = user?.stripeId;

  const [logs,          setLogs]          = useState([]);
  const [status,        setStatus]        = useState("idle");
  const [errorMsg,      setErrorMsg]      = useState("");
  const [lastFetch,     setLastFetch]     = useState(null);
  const [nextRefreshIn, setNextRefreshIn] = useState(null);

  const timerRef     = useRef(null);
  const countdownRef = useRef(null);
  const nextTickRef  = useRef(null);

  const fetchLogs = useCallback(
    async (retryCounts = 1) => {
      if (!tenantId) {
        setStatus("error");
        setErrorMsg("User session not found (missing tenantId).");
        return;
      }
      if (document.visibilityState === "hidden") return;

      const accessToken = await handleFetchAuth0Token();
      const now         = Date.now();
      const start       = now - (timeRange?.ms ?? TIME_RANGES[5].ms);

      setStatus("loading");

      try {
        const response = await axios({
          method: "post",
          url:    getEnv("EXECUTOR_API"),
          maxBodyLength: Infinity,
          data: {
            query:     GET_LOGS_QUERY,
            variables: { tenantId, flowId, start: String(start), end: String(now) },
          },
          headers: {
            "Content-Type":             "application/json",
            "Apollo-Require-Preflight": true,
            Authorization:              "Bearer " + accessToken,
            withCredentials:            true,
            identifier:                 uuidv4(),
          },
        });

        const result    = response.data?.data?.getExecutionLogs;
        const gqlErrors = response.data?.errors;

        if (gqlErrors?.length > 0) throw new Error(gqlErrors[0].message);
        if (!result)                throw new Error("No response from log server.");

        const rawLogs = Array.isArray(result) ? result : (result.logs ?? []);
        const mapped  = rawLogs.map(mapLogItem).sort(sortById);

        setLogs(mapped);
        setStatus("live");
        setLastFetch(new Date());
        setErrorMsg("");
      } catch (err) {
        console.error("fetchLogs error", err);
        if (err.response?.status === 502 && retryCounts < 3) return fetchLogs(retryCounts + 1);
        if (err.response) toast.error("Oops! Something went wrong on our end.");
        setStatus("error");
        setErrorMsg(err.message || "Unknown error fetching logs.");
      }
    },
    [tenantId, flowId, timeRange]
  );

  const startCountdown = useCallback((intervalMs) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (!intervalMs) { setNextRefreshIn(null); return; }
    nextTickRef.current = Date.now() + intervalMs;
    setNextRefreshIn(Math.ceil(intervalMs / 1000));
    countdownRef.current = setInterval(() => {
      const remaining = nextTickRef.current - Date.now();
      setNextRefreshIn(remaining > 0 ? Math.ceil(remaining / 1000) : 0);
    }, 500);
  }, []);

  useEffect(() => {
    fetchLogs();

    if (autoRefreshInterval > 0) {
      startCountdown(autoRefreshInterval);
      timerRef.current = setInterval(() => {
        if (document.visibilityState === "hidden") {
          nextTickRef.current = Date.now() + autoRefreshInterval;
          return;
        }
        fetchLogs();
        nextTickRef.current = Date.now() + autoRefreshInterval;
      }, autoRefreshInterval);
    } else {
      startCountdown(0);
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && autoRefreshInterval > 0) {
        nextTickRef.current = Date.now() + autoRefreshInterval;
        startCountdown(autoRefreshInterval);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (timerRef.current)     clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [fetchLogs, autoRefreshInterval, startCountdown]);

  const refresh = useCallback(() => {
    fetchLogs();
    if (autoRefreshInterval > 0) {
      nextTickRef.current = Date.now() + autoRefreshInterval;
      startCountdown(autoRefreshInterval);
    }
  }, [fetchLogs, autoRefreshInterval, startCountdown]);

  const clearLogs = useCallback(() => {
    setLogs([]); setStatus("idle");
  }, []);

  const downloadLogs = useCallback(
    (format = "json") => {
      if (!logs.length) { toast.warn("No logs to download."); return; }
      let content, mimeType;
      switch (format) {
        case "json":
          content  = JSON.stringify(logs, null, 2);
          mimeType = "application/json";
          break;
        case "csv": {
          const allFieldKeys = new Set();
          logs.forEach((l) => Object.keys(l.fields || {}).forEach((k) => allFieldKeys.add(k)));
          const fieldKeys = [...allFieldKeys].filter((k) => k !== "tenant_id");
          const headers   = ["time", "level", "line", ...fieldKeys];
          const rows      = logs.map((l) => [
            l.time, l.level, l.line,
            ...fieldKeys.map((k) => l.fields?.[k] ?? ""),
          ]);
          content  = [headers, ...rows]
            .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
            .join("\n");
          mimeType = "text/csv";
          break;
        }
        case "txt":
          content  = logs.map((l) => `[${l.time}] [${l.level.toUpperCase().padEnd(5)}] ${l.line}`).join("\n");
          mimeType = "text/plain";
          break;
        default:
          toast.error(`Unknown format: ${format}`);
          return;
      }
      const blob = new Blob([content], { type: mimeType });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href     = url;
      link.download = `logs-${tenantId}-${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    [logs, tenantId]
  );

  return {
    logs,
    total: logs.length,
    status,
    errorMsg,
    lastFetch,
    nextRefreshIn,
    refresh,
    clearLogs,
    downloadLogs,
  };
}